import React from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Paper,
  Title,
  Divider,
  Grid,
  Card,
  Alert,
  Anchor,
  Accordion
} from '@mantine/core';
import {
  IconVideo,
  IconLivePhoto,
  IconQuestionMark,
  IconDownload,
  IconCalendar,
  IconEye,
  IconKey,
  IconFileText, IconBrandYoutube // <-- Add this

} from '@tabler/icons-react';

const ClassDetails = ({ classItem }) => {
  const InfoCard = ({ icon: Icon, title, value, color = 'blue' }) => (
    <Card withBorder p="md">
      <Group>
        <Icon size={24} color={`var(--mantine-color-${color}-6)`} />
        <Stack gap="xs">
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text size="sm" fw={600}>
            {value}
          </Text>
        </Stack>
      </Group>
    </Card>
  );

  const getTypeBadge = () => {
    return classItem.type === 'video_class'
      ? <Badge color="blue" size="lg" leftSection={<IconVideo size={16} />}>Video Class</Badge>
      : <Badge color="green" size="lg" leftSection={<IconLivePhoto size={16} />}>Live Class</Badge>;
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>{classItem.title}</Title>
        {getTypeBadge()}
      </Group>

      <Paper withBorder p="md">
        <Stack gap="xs">
          <Text fw={600}>Short Description</Text>
          <Text size="sm">{classItem.short_description}</Text>
        </Stack>
      </Paper>

      {classItem.long_description && (
        <Paper withBorder p="md">
          <Stack gap="xs">
            <Text fw={600}>Detailed Description</Text>
            <Text size="sm">{classItem.long_description}</Text>
          </Stack>
        </Paper>
      )}
      {/* ADD THIS BLOCK */}
      {classItem.youtube_link && (
        <Paper withBorder p="md">
          <Group>
            <IconBrandYoutube size={24} color="red" />
            <Stack gap="xs">
              <Text size="sm" c="dimmed" fw={500}>Optional YouTube Link attached</Text>
              <Anchor href={classItem.youtube_link} target="_blank" size="sm">
                {classItem.youtube_link}
              </Anchor>
            </Stack>
          </Group>
        </Paper>
      )}

      <Divider label="Class Configuration" labelPosition="center" />

      {classItem.type === 'video_class' ? (
        <Stack>
          <Grid>
            <Grid.Col span={6}>
              <InfoCard
                icon={IconVideo}
                title="Bunny Stream Video ID"
                value={classItem.vdocipher_video_id || 'Not Set'}
                color="blue"
              />
              {classItem.hls_link ? (
                <Grid.Col span={6}>
                  <InfoCard
                    icon={IconVideo}
                    title="HLS Stream Link"
                    value="Available"
                    color="green"
                  />
                </Grid.Col>
              ) : (<span style={{ color: 'red' }}>HLS Stream Link not available</span>)}
            </Grid.Col>
            <Grid.Col span={6}>
              <InfoCard
                icon={IconQuestionMark}
                title="Quiz Pass Rate"
                value={classItem.quiz_pass_rate ? `${classItem.quiz_pass_rate}%` : 'No Quiz'}
                color="purple"
              />
            </Grid.Col>
          </Grid>

          {classItem.quiz_json && classItem.quiz_json.length > 0 && (
            <>
              <Divider label="Quiz Questions" labelPosition="center" />
              <Accordion>
                {classItem.quiz_json.map((question, index) => (
                  <Accordion.Item key={index} value={`question-${index}`}>
                    <Accordion.Control>
                      <Group>
                        <IconQuestionMark size={16} />
                        <Text fw={500}>Question {index + 1}</Text>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="sm">
                        <Paper withBorder p="sm">
                          <Text fw={600} size="sm">Question:</Text>
                          <Text size="sm">{question.question}</Text>
                        </Paper>

                        <Text fw={600} size="sm">Options:</Text>
                        {question.options?.map((option, optIndex) => (
                          <Paper
                            key={optIndex}
                            withBorder
                            p="xs"
                            style={{
                              backgroundColor: optIndex === question.correct_answer
                                ? 'var(--mantine-color-green-0)'
                                : undefined
                            }}
                          >
                            <Group>
                              <Badge
                                size="xs"
                                color={optIndex === question.correct_answer ? 'green' : 'gray'}
                              >
                                {String.fromCharCode(65 + optIndex)}
                              </Badge>
                              <Text size="sm">{option}</Text>
                              {optIndex === question.correct_answer && (
                                <Badge size="xs" color="green">Correct</Badge>
                              )}
                            </Group>
                          </Paper>
                        ))}

                        {question.explanation && (
                          <Paper withBorder p="sm" style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                            <Text fw={600} size="sm">Explanation:</Text>
                            <Text size="sm">{question.explanation}</Text>
                          </Paper>
                        )}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </>
          )}

          {classItem.downloadable_material_links_json && classItem.downloadable_material_links_json.length > 0 && (
            <>
              <Divider label="Downloadable Materials" labelPosition="center" />
              <Stack>
                {classItem.downloadable_material_links_json.map((material, index) => (
                  <Card key={index} withBorder p="md">
                    <Group justify="space-between">
                      <Group>
                        <IconDownload size={20} color="var(--mantine-color-orange-6)" />
                        <Stack gap="xs">
                          <Text fw={600} size="sm">{material.filename}</Text>
                          <Text size="xs" c="dimmed">
                            Size: {(material.size / 1024 / 1024).toFixed(2)} MB
                          </Text>
                        </Stack>
                      </Group>
                      <Anchor href={material.url} target="_blank">
                        <Badge color="orange" leftSection={<IconDownload size={12} />}>
                          Download
                        </Badge>
                      </Anchor>
                    </Group>
                  </Card>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      ) : (
        <Stack>
          <Grid>
            <Grid.Col span={6}>
              <Paper withBorder p="md">
                <Group>
                  <IconLivePhoto size={24} color="var(--mantine-color-green-6)" />
                  <Stack gap="xs">
                    <Text size="sm" c="dimmed" fw={500}>Live Stream Link</Text>
                    <Anchor href={classItem.live_link} target="_blank" size="sm">
                      {classItem.live_link}
                    </Anchor>
                  </Stack>
                </Group>
              </Paper>
            </Grid.Col>
            <Grid.Col span={6}>
              <InfoCard
                icon={IconKey}
                title="Passkey"
                value={classItem.passkey || 'No Passkey'}
                color="orange"
              />
            </Grid.Col>
          </Grid>

          {classItem.thumbnail_image && (
            <Paper withBorder p="md">
              <Group>
                <IconEye size={24} color="var(--mantine-color-blue-6)" />
                <Stack gap="xs">
                  <Text size="sm" c="dimmed" fw={500}>Thumbnail Image</Text>
                  <Anchor href={classItem.thumbnail_image} target="_blank" size="sm">
                    View Thumbnail
                  </Anchor>
                </Stack>
              </Group>
            </Paper>
          )}
        </Stack>
      )}

      <Divider label="System Information" labelPosition="center" />

      <Grid>
        <Grid.Col span={6}>
          <Paper withBorder p="md">
            <Group>
              <IconCalendar size={24} color="var(--mantine-color-gray-6)" />
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={500}>Created</Text>
                <Text size="sm">
                  {new Date(classItem.created_at).toLocaleString('en-IN', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </Text>
              </Stack>
            </Group>
          </Paper>
        </Grid.Col>
        <Grid.Col span={6}>
          <Paper withBorder p="md">
            <Group>
              <IconFileText size={24} color="var(--mantine-color-gray-6)" />
              <Stack gap="xs">
                <Text size="sm" c="dimmed" fw={500}>Last Updated</Text>
                <Text size="sm">
                  {new Date(classItem.updated_at).toLocaleString('en-IN', {
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })}
                </Text>
              </Stack>
            </Group>
          </Paper>
        </Grid.Col>
      </Grid>

      <Alert color="blue">
        <Text size="sm">
          This class can be added to curriculums and assigned to batches.
          {classItem.type === 'video_class' && classItem.quiz_json?.length > 0 &&
            ` Students must achieve ${classItem.quiz_pass_rate}% or higher to progress to the next class.`
          }
        </Text>
      </Alert>
    </Stack>
  );
};

export default ClassDetails;